"use client";

import { VideoModal } from "@contentful/components";
import { TypeCtaSkeleton, TypeHero } from "@contentful/types";
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { notEmpty } from "@yardzen/js-util";
import { navigateCtaUrl } from "@yardzen/next-client-util";
import { Button } from "@yardzen/ui/components/button";
import { cn } from "@yardzen/ui/lib/utils";
import { cva } from "class-variance-authority";
import { Entry } from "contentful";
import { useRouter } from "next/navigation";

export function HeroActions({
  hero,
  className,
}: {
  hero: TypeHero<"WITHOUT_UNRESOLVABLE_LINKS">;
  className?: string;
}) {
  const router = useRouter();
  const calloutLocation = hero.fields.calloutLocation || "lower-left";
  const textColor = hero?.fields.textColor || "default";

  const locationVariants = cva("", {
    variants: {
      wrapperClass: {
        "bottom-middle":
          "md:mx-auto -mt-7 mb-0 sm:mt-0 backdrop-blur-2xl sm:absolute top-auto bottom-0 lg:w-[1070px] bg-opacity-10 md:items-center rounded-t-2xl py-6 px-2 sm:px-6",
        "lower-left":
          "-mt-10 md:absolute md:inset-auto md:bottom-20 md:left-4 md:z-10 md:mx-0 md:mb-0 md:mt-0 md:w-[35%] bg-opacity-70 md:items-start rounded-2xl p-6",
      },
      headClass: {
        "bottom-middle": "md:text-[42px] text-center text-brand-primary-darker",
        "lower-left": "md:text-[42px]",
      },
      subheadClass: {
        "bottom-middle":
          "text-center text-brand-moss-greens-90 leading-[120%] mb-0",
        "lower-left": "",
      },
      textColor: {
        white: "sm:text-brand-beige-lighter text-brand-beige-lighter",
        black: "sm:text-brand-primary-darker text-brand-primary-darker",
        default: "sm:text-scheme-text-primary text-scheme-text-primary",
      },
      buttonClass: {
        "bottom-middle": "",
        "lower-left": "",
      },
    },
  });

  return (
    <div
      className={cn(
        "bg-system-grayscale-00 relative inset-0 z-10 flex flex-col items-center gap-4 backdrop-blur-lg",
        locationVariants({ wrapperClass: calloutLocation }),
        className,
      )}
    >
      {hero.fields.customStyles && (
        <style dangerouslySetInnerHTML={{ __html: hero.fields.customStyles }} />
      )}
      <div className="relative text-center text-white md:text-left">
        {hero.fields.eyebrowImage?.fields?.file?.url && (
          <div className="mb-4 flex justify-center md:justify-start">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={`https:${hero.fields.eyebrowImage.fields.file.url}`}
              alt={
                hero.fields.eyebrowImage.fields.description ??
                hero.fields.eyebrowImage.fields.title ??
                ""
              }
              style={{
                maxWidth: `min(${hero.fields.eyebrowImageWidth ?? 180}px, 100%)`,
              }}
              className="h-auto w-auto"
            />
          </div>
        )}
        <div className="mb-4">
          <h1
            className={cn(
              "font-display mb-1 text-3xl",
              locationVariants({ textColor }),
              locationVariants({ headClass: calloutLocation }),
            )}
          >
            {hero.fields.title}
          </h1>
        </div>
        <p
          className={cn(
            "mb-2",
            locationVariants({ textColor }),
            locationVariants({ subheadClass: calloutLocation }),
          )}
        >
          {hero.fields.subtitle}
        </p>
      </div>
      <div className="relative flex w-full flex-col items-center gap-4 sm:w-auto sm:flex-row">
        {hero.fields.CTAs?.filter(notEmpty).map((cta, i) => (
          <CTA key={`cta-${cta.sys.id}-${i}`} cta={cta} />
        ))}
        {hero.fields.cta && <CTA cta={hero.fields.cta} />}
      </div>
    </div>
  );

  function CTA({
    cta,
  }: {
    cta?:
      | Entry<TypeCtaSkeleton, "WITHOUT_UNRESOLVABLE_LINKS", string>
      | undefined;
  }) {
    let props = {};
    if (!cta) {
      return null;
    }
    if (cta.fields.variant === "default") {
      props = {
        endIcon: <FontAwesomeIcon icon={faArrowRight} />,
      };
    }

    const url = cta.fields.url;

    if (cta.fields.videoLink && cta.fields.url) {
      return <VideoModal key={cta.sys.id} contentfulCTA={cta} />;
    }

    return (
      <Button
        key={cta.sys.id}
        className="w-full px-4 py-3 sm:w-auto"
        variant={cta.fields.variant}
        onClick={async () => {
          if (cta.fields.openInNewTab && url) {
            window.open(url, "_blank", "noopener,noreferrer");
            return;
          }
          navigateCtaUrl(url, router);
        }}
        {...props}
      >
        {cta.fields.buttonText}
      </Button>
    );
  }
}
